import { Switch } from "@headlessui/react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/Button";
import GoogleLoginButton from "../components/GoogleLoginButton";
import PrivacyPolicyModal from "../components/modals/PrivacyPolicy";
import { getCourseModules, getNextClass } from "../services/CourseModuleService";
import { createAccount } from "../services/LoginService";
import { setCookie } from "../utils/cookies";

export default function SignupPage() {
    const [searchParams] = useSearchParams();
    const origin = searchParams.get("origin");
    const redirectVideo = searchParams.get("redirect");
    const [enabled, setEnabled] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [nameError, setNameError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [policyError, setPolicyError] = useState(false);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [url, setUrl] = useState("");
    const [newPassword, setNewPassword] = useState<string>("");
    const [newPasswordError, setNewPasswordError] = useState<string>("");
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleCheckPassword = ({ onChange, value }: { onChange: (val: string) => void; value: string }) => {
        if (!/^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(value)) {
            setNewPasswordError("A senha deve conter pelo menos 8 caracteres, uma letra e um número");
        } else {
            setNewPasswordError("");
        }
        onChange(value);
    }

    const formatPhoneNumber = (value: string) => {
        // Remove qualquer caractere que não seja número
        const cleanedValue = value.replace(/\D/g, '');

        // Aplica a formatação para telefones fixos e celulares
        if (cleanedValue.length <= 10) {
            // Formato para telefone fixo: (XX) XXXX-XXXX
            return cleanedValue.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        } else {
            // Formato para celular: (XX) XXXXX-XXXX
            return cleanedValue.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            if (!enabled) {
                setPolicyError(true);
            } else {
                setPolicyError(false);
            }
            if (name === "" || (name && name.split(" ").length < 2)) {
                setNameError("Preencha seu nome completo");
            } else {
                setNameError("");
            }
            if (newPassword === "" || !/^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(newPassword)) {
                setNewPasswordError("A senha deve conter pelo menos 8 caracteres, uma letra e um número");
            } else {
                setNewPasswordError("");
            }
            if (email === "" || !validateEmail(email)) {
                setEmailError("Informe um e-mail válido");
            } else {
                setEmailError("");
            }
            if (phone === "") {
                setPhoneError("Preencha seu telefone");
            } else {
                setPhoneError("");
            }
            if (name === "" || email === "" || phone === "" || !enabled || !validateEmail(email) || newPassword === "") {
                return;
            }
            const first_name = name.split(" ")[0];
            const last_name = name.split(" ")[1];
            const response = await createAccount({ first_name, ...(origin && { origin }), last_name, email, phone, password: newPassword });
            try {
                if (typeof window.fbq !== 'undefined') {
                    window.fbq('track', 'Lead');
                }
            } catch (e) {
                // Ignore errors with fbq
            }
            if (response.status === 201) {
                setCookie("token", response.data.access_token);
                try {
                    if (redirectVideo) {
                        const { data } = await getCourseModules({ course: redirectVideo, page: 1, page_size: 1 });
                        if (data.results.length > 0) {
                            const { data: nextClass } = await getNextClass(data.results[0].id);
                            if (nextClass) {
                                navigate(`/curso/${redirectVideo}/modulo/${nextClass.module_id}/aula/${nextClass.class_id}`);
                            } else {
                                navigate(`/curso/${redirectVideo}`);
                            }
                        } else {
                            navigate(`/curso/${redirectVideo}`);
                        }
                    } else {
                        navigate("/inicio");
                    }
                } catch (e) {
                    navigate("/inicio");
                }
            }
            setError("");
        } catch (e) {
            setError("Erro ao enviar formulário, tente novamente mais tarde");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="relative w-full min-h-screen flex items-center justify-center  py-12 px-6 sm:px-8 lg:px-8">
            <PrivacyPolicyModal open={open} onClose={() => setOpen(false)} />


            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <img
                        src="/images/logo-blue.png"
                        alt="Logo"
                        className="h-16 mx-auto mb-4 cursor-pointer"
                        onClick={() => navigate("/home")}
                    />
                    <h2 className="text-3xl font-bold tracking-tight sm:text-3xl mb-2">
                        Crie sua conta
                    </h2>
                    <p className="text-md text-gray-600">
                        Tenha acesso a recursos exclusivos para organizar suas finanças
                    </p>
                </div>

                <div className="bg-white py-8 px-6 shadow rounded-lg sm:px-10">
                    <div className="space-y-5">
                        <GoogleLoginButton buttonText="Cadastrar com Google" />

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500">ou</span>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="nome" className="block text-sm font-medium text-gray-700">
                                Nome Completo
                            </label>
                            <input
                                id="nome"
                                name="nome"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Nome completo"
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            />
                            {nameError && <p className="mt-1 text-sm text-red-600">{nameError}</p>}
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                Email
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="email@exemplo.com"
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            />
                            {emailError && <p className="mt-1 text-sm text-red-600">{emailError}</p>}
                        </div>

                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                Telefone
                            </label>
                            <input
                                id="phone"
                                name="phone"
                                type="text"
                                value={phone}
                                onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                                placeholder="(xx) xxxxx-xxxx"
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            />
                            {phoneError && <p className="mt-1 text-sm text-red-600">{phoneError}</p>}
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                Senha de acesso
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                                <input
                                    id="password"
                                    value={newPassword}
                                    placeholder="********"
                                    onChange={(e) => handleCheckPassword({ onChange: setNewPassword, value: e.target.value })}
                                    type={showPassword ? "text" : "password"}
                                    className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm pr-10"
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                    {!showPassword && <EyeIcon onClick={() => setShowPassword(true)} className="h-5 w-5 text-gray-400 cursor-pointer" />}
                                    {showPassword && <EyeSlashIcon onClick={() => setShowPassword(false)} className="h-5 w-5 text-gray-400 cursor-pointer" />}
                                </div>
                            </div>
                            {newPasswordError && <p className="mt-1 text-sm text-red-600">{newPasswordError}</p>}
                        </div>

                        <div className="flex items-center">
                            <Switch
                                checked={enabled}
                                onChange={setEnabled}
                                className={`${enabled ? 'bg-blue-600' : 'bg-gray-200'
                                    } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                            >
                                <span className="sr-only">Aceitar política de privacidade</span>
                                <span
                                    aria-hidden="true"
                                    className={`${enabled ? 'translate-x-5' : 'translate-x-0'
                                        } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                                />
                            </Switch>
                            <span className="ml-3 text-sm">
                                <span className="font-medium text-gray-700">Eu concordo com a </span>
                                <span onClick={() => setOpen(true)} className="font-medium text-blue-600 cursor-pointer hover:underline">
                                    política de privacidade
                                </span>
                            </span>
                        </div>
                        {policyError && <p className="text-sm text-red-600">Para cadastrar-se é necessário aceitar os termos</p>}

                        <div>
                            <Button
                                disabled={loading}
                                loading={loading}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                onClick={handleSubmit}
                                title="Criar conta"
                            />
                        </div>

                        {error && <p className="text-center text-sm text-red-600">{error}</p>}
                    </div>

                    <div className="mt-6 text-center">
                        <p className="text-sm text-gray-600">
                            Já possui uma conta?{' '}
                            <button
                                onClick={() => navigate("/login")}
                                className="font-medium text-blue-600 hover:text-blue-500"
                            >
                                Faça login
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}